import type { InspirationDTO } from "@zplanem/responses"
import { useState } from "react"
import {
	useInspirations,
	useInspirationsRealtime,
} from "../../hooks/use-inspirations"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import { InspirationCard } from "./inspiration-card"
import { InspirationModal } from "./inspiration-modal"
import { modalNeighbors } from "./media-modal-nav"
import { useProjectDetailContext } from "./project-detail-context"
import { ProjectMediaAddModal } from "./project-media-add-modal"

function InspirationsIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="w-10 h-10"
			aria-hidden="true"
		>
			<path d="M9 18h6" />
			<path d="M10 21h4" />
			<path d="M12 3a6 6 0 0 0-3.6 10.8c.5.4.8.9.9 1.5l.1.7h5.2l.1-.7c.1-.6.4-1.1.9-1.5A6 6 0 0 0 12 3Z" />
		</svg>
	)
}

function PlusIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			className="w-5 h-5"
			aria-hidden="true"
		>
			<path d="M12 5v14M5 12h14" />
		</svg>
	)
}

/**
 * Inspirations sub-tab (ZPL-127/132/270): a responsive 1–5 column grid of
 * thumbnail cards. The add control is a small right-aligned button above the
 * grid when there are items (documents pattern, ZPL-270), and a centered
 * borderless button inside the empty-state box when there are none. Clicking
 * a card opens a url / pdf in a new tab, or an image/video in the modal.
 * The list refetches live via realtime.
 */
export function ProjectInspirationsTab() {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const { propertyId, project } = useProjectDetailContext()
	const { data, isLoading, isError } = useInspirations(propertyId, project.id)
	useInspirationsRealtime(propertyId, project.id)
	const [uploadOpen, setUploadOpen] = useState(false)
	const [active, setActive] = useState<InspirationDTO | null>(null)

	if (isLoading) {
		return (
			<div className="py-12 text-center text-sm text-muted-foreground">
				{t.common.loading}
			</div>
		)
	}
	// Not while the add modal is open: each confirmed upload fires a realtime
	// `inspiration.changed` that refetches the list, and one failed background
	// refetch would otherwise unmount the modal mid-batch, destroying the
	// per-row done/failed state and the retry affordance. react-query keeps
	// the previous data, so the grid stays usable.
	if (isError && !uploadOpen) {
		return (
			<div className="py-12 text-center text-sm text-destructive">
				{t.projects.inspirations.errorLoad}
			</div>
		)
	}

	const items = data ?? []
	// ZPL-267: prev/next cycles only through modal-viewable items (image/video).
	const activeNeighbors = active ? modalNeighbors(items, active.id) : null

	function openInspiration(insp: InspirationDTO) {
		if (insp.kind === "url" && insp.url) {
			window.open(insp.url, "_blank", "noopener,noreferrer")
			return
		}
		// PDFs open in a new tab; images/videos open in the modal.
		if (insp.kind === "pdf" && insp.sourceUrl) {
			window.open(insp.sourceUrl, "_blank", "noopener,noreferrer")
			return
		}
		setActive(insp)
	}

	return (
		<div className="space-y-4">
			{items.length === 0 ? (
				<div className="rounded-xl border border-dashed border-border bg-muted/30 px-6 py-16 flex flex-col items-center text-center">
					<div className="text-muted-foreground/70 mb-3">
						<InspirationsIcon />
					</div>
					<h2 className="text-base font-bold text-foreground">
						{t.projects.inspirations.emptyTitle}
					</h2>
					<p className="mt-1.5 text-sm text-muted-foreground/80 max-w-sm">
						{t.projects.inspirations.emptyBody}
					</p>
					<button
						type="button"
						onClick={() => setUploadOpen(true)}
						className="mt-6 inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
					>
						<PlusIcon />
						{t.projects.inspirations.addCta}
					</button>
				</div>
			) : (
				<>
					<div className="flex items-center justify-end">
						<button
							type="button"
							onClick={() => setUploadOpen(true)}
							className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
						>
							<PlusIcon />
							{t.projects.inspirations.addCta}
						</button>
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
						{items.map((insp) => (
							<InspirationCard
								key={insp.id}
								inspiration={insp}
								propertyId={propertyId}
								projectId={project.id}
								onOpen={() => openInspiration(insp)}
							/>
						))}
					</div>
				</>
			)}

			{uploadOpen ? (
				<ProjectMediaAddModal
					collection="inspiration"
					propertyId={propertyId}
					projectId={project.id}
					existingCount={items.length}
					onClose={() => setUploadOpen(false)}
				/>
			) : null}
			{active ? (
				<InspirationModal
					// Remount per item so the modal's local edit state resets when
					// navigating (ZPL-267).
					key={active.id}
					inspiration={active}
					propertyId={propertyId}
					projectId={project.id}
					onClose={() => setActive(null)}
					onPrev={
						activeNeighbors ? () => setActive(activeNeighbors.prev) : undefined
					}
					onNext={
						activeNeighbors ? () => setActive(activeNeighbors.next) : undefined
					}
				/>
			) : null}
		</div>
	)
}
